import { useEffect, useState } from "react";

interface Props {
  /** id of the scrollable element. Defaults to `doc-main-content`. */
  targetId?: string;
}

/**
 * B3.4 — Reading progress bar.
 *
 * Sticky 2px bar at the top of the viewer that reflects scroll progress of
 * the document's main scroll container. Listens passive to scroll on the
 * target element.
 */
export function ReadingProgress({ targetId = "doc-main-content" }: Props) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const el = document.getElementById(targetId);
    if (!el) return;
    function compute() {
      const target = el as HTMLElement;
      const total = target.scrollHeight - target.clientHeight;
      if (total <= 0) {
        setProgress(0);
        return;
      }
      const ratio = Math.min(1, Math.max(0, target.scrollTop / total));
      setProgress(ratio * 100);
    }
    compute();
    el.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      el.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [targetId]);

  return (
    <div
      data-testid="reading-progress"
      className="reading-progress sticky top-0 z-10 h-[2px] w-full bg-transparent pointer-events-none"
    >
      <div
        className="h-full bg-(--accent-500) transition-[width] duration-75 ease-linear"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}
